import { useCallback, useEffect, useRef } from 'react'

import { useLiveChannelState, useLiveUserDevicesState } from './LiveClientProvider'

const SOUNDS = {
    JOIN: '/sounds/funk.wav',
    LEAVE: '/sounds/bottle.wav',
    STAGE_ADD: '/sounds/heads_up.wav',
    LISTENER_ADD: '/sounds/glassy-touch.mp3',
}

// Duration in ms before we start tracking join sounds
const INITIAL_GRACE_PERIOD_MS = 1500
const LISTENER_ALERT_THRESHOLD = 10

// Audio cache to avoid creating new Audio elements each time
const audioCache: Record<string, HTMLAudioElement> = {}

function getAudio(url: string | undefined): HTMLAudioElement | undefined {
    if (!url || typeof window === 'undefined') {
        return undefined
    }
    if (!audioCache[url]) {
        audioCache[url] = new Audio(url)
        audioCache[url].preload = 'auto'
    }
    return audioCache[url]
}

/**
 * Preload all sounds by creating Audio elements and letting the browser fetch them.
 * This runs once and uses requestIdleCallback to avoid blocking the main thread.
 */
function preloadAllSounds(): void {
    const preload = () => {
        for (const url of Object.values(SOUNDS)) {
            getAudio(url) // Creates and caches the Audio element with preload='auto'
        }
    }

    // Use requestIdleCallback if available, otherwise setTimeout
    if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(preload)
    } else {
        setTimeout(preload, 100)
    }
}

// Trigger preload when this module loads (browser only)
if (typeof window !== 'undefined') {
    preloadAllSounds()
}

function playSound(url: string | undefined): void {
    const audio = getAudio(url)
    if (!audio) {
        return
    }
    // Reset to start in case it's already playing
    audio.currentTime = 0
    audio.play().catch((error) => {
        // Autoplay may be blocked by browser - this is expected
        console.debug('[useLiveStageSounds] Could not play sound:', error)
    })
}

/**
 * Hook that monitors the stage and plays sounds for various live events:
 * - Wake up sound when first entering a channel
 * - Stage join sound when users join the stage (after initial grace period)
 * - Listener join sound when users join as listeners (when stage is not full)
 *
 * Resets state whenever the channel ID changes.
 */
export function useLiveStageSounds(): void {
    const devicesState = useLiveUserDevicesState()
    const { deviceState } = devicesState ?? {}

    // Get the current live channel info from device state
    const isLive = deviceState?.type === 'live'
    const spaceId = isLive ? deviceState.spaceId : undefined
    const channelId = isLive ? deviceState.channelId : undefined

    const channelState = useLiveChannelState(spaceId, channelId)

    // Track the previous channel ID to detect channel changes
    const prevChannelIdRef = useRef<string | undefined>(undefined)

    // Track whether we've completed the initial grace period
    const isGracePeriodCompleteRef = useRef(false)

    // Track previous counts for detecting joins
    const prevStageCountRef = useRef<number>(0)
    const prevListenerCountRef = useRef<number>(0)

    // Grace period timer reference
    const gracePeriodTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Reset all tracking state when channel changes
    const resetState = useCallback(() => {
        if (gracePeriodTimerRef.current) {
            clearTimeout(gracePeriodTimerRef.current)
            gracePeriodTimerRef.current = null
        }
        isGracePeriodCompleteRef.current = false
        prevStageCountRef.current = 0
        prevListenerCountRef.current = 0
        console.log('[useLiveStageSounds] State reset')
    }, [])

    // Effect to handle channel ID changes
    useEffect(() => {
        const prevChannelId = prevChannelIdRef.current

        // Detect channel change
        if (channelId !== prevChannelId) {
            console.log('[useLiveStageSounds] Channel changed:', prevChannelId, '->', channelId)
            if (prevChannelId) {
                playSound(SOUNDS.LEAVE)
            }
            resetState()
            prevChannelIdRef.current = channelId
        }
    }, [channelId, resetState])

    // Effect to handle initial channel entry - only depends on channelId, not channelState
    // This starts the grace period timer when entering a new channel
    useEffect(() => {
        // Not in a live channel
        if (!isLive || !channelId) {
            return
        }

        // Play wake-up sound on initial channel entry
        console.log('[useLiveStageSounds] Playing wake-up sound for channel:', channelId)
        playSound(SOUNDS.JOIN)

        // Start the grace period timer - this should NOT be canceled when channelState changes
        gracePeriodTimerRef.current = setTimeout(() => {
            console.log('[useLiveStageSounds] Grace period complete, now tracking joins')
            isGracePeriodCompleteRef.current = true
        }, INITIAL_GRACE_PERIOD_MS)

        // Only cleanup when channelId changes (which triggers resetState anyway)
        return () => {
            if (gracePeriodTimerRef.current) {
                clearTimeout(gracePeriodTimerRef.current)
                gracePeriodTimerRef.current = null
            }
        }
    }, [isLive, channelId])

    // Effect to monitor stage and listener joins
    // Always updates counts to stay current, but only plays sounds after grace period
    useEffect(() => {
        if (!isLive || !channelState) {
            return
        }

        const currentStageCount = channelState.stage.totalCount
        const currentListenerCount = channelState.listeners.totalCount
        const prevStageCount = prevStageCountRef.current
        const prevListenerCount = prevListenerCountRef.current

        // Only play sounds after grace period is complete
        if (isGracePeriodCompleteRef.current) {
            // Check for new stage joins
            if (currentStageCount > prevStageCount) {
                console.log(
                    '[useLiveStageSounds] New user(s) joined stage:',
                    prevStageCount,
                    '->',
                    currentStageCount,
                )
                playSound(SOUNDS.STAGE_ADD)
            }

            // Check for new listener joins (only for first few listeners)
            if (
                currentListenerCount > prevListenerCount &&
                currentListenerCount < LISTENER_ALERT_THRESHOLD
            ) {
                console.log(
                    '[useLiveStageSounds] New listener(s) joined (stage not full):',
                    prevListenerCount,
                    '->',
                    currentListenerCount,
                )
                playSound(SOUNDS.LISTENER_ADD)
            }
        }

        // Always update refs to stay current (even during grace period)
        prevStageCountRef.current = currentStageCount
        prevListenerCountRef.current = currentListenerCount
    }, [channelState, isLive])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (gracePeriodTimerRef.current) {
                clearTimeout(gracePeriodTimerRef.current)
            }
        }
    }, [])
}
